import React from 'react';
import { Helmet } from 'react-helmet-async';
import SEOHead from './SEOHead';
import {
  Box,
  Container,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import usePublicHelpDocs from '../hooks/usePublicHelpDocs';
import { PRE_SIGNUP_FAQ } from '../content/marketingContent';

const SUPPORT_EMAIL = 'support@resellerprosoftware.com';

const faqJsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: PRE_SIGNUP_FAQ.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
});

const PublicHelpAndSupport = ({ onLoginClick }) => {
  const { helpCategories, loading, error } = usePublicHelpDocs();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <SEOHead pageKey="/help" />
      <Helmet>
        <script type="application/ld+json">{faqJsonLd}</script>
      </Helmet>
      {/* Header/Navigation */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          boxShadow: 1,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => navigate('/')}
          role="link"
          aria-label="Go to home page"
        >
          <Box component="img" src="/rp-barcode-icon.svg" alt="Reseller Pro" sx={{ height: 32, width: 'auto', display: { xs: 'block', md: 'none' } }} />
          <Box component="img" src="/rp-barcode-text-1024x167.svg" alt="Reseller Pro" sx={{ height: 40, width: 'auto', display: { xs: 'none', md: 'block' } }} />
        </Box>
        <Box component="nav" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <Button
            variant="text"
            component={RouterLink}
            to="/pricing"
            sx={{ textTransform: 'none', fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'primary.main' }}
          >
            Pricing
          </Button>
          <Button
            variant="text"
            onClick={() => navigate('/help')}
            sx={{ textTransform: 'none', fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'primary.main' }}
          >
            Help &amp; Support
          </Button>
          <Button
            variant="contained"
            onClick={onLoginClick}
            sx={{ textTransform: 'none', fontSize: '1rem' }}
          >
            Sign In
          </Button>
        </Box>
      </Box>

      {/* Help Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 700, mb: 2, color: 'heading.main' }}
        >
          Help &amp; Support
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Find helpful information about how to use Reseller Pro. Click on any section below to expand and learn more.
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Pre-signup questions, bundled so they render even if the help API is unreachable */}
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 2, color: 'heading.main' }}>
          Before You Sign Up
        </Typography>
        {PRE_SIGNUP_FAQ.map((item) => (
          <Accordion key={item.question} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {item.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}

        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mt: 6, mb: 2, color: 'heading.main' }}>
          Using Reseller Pro
        </Typography>

        {loading && (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            More help articles couldn't load right now. Please refresh the page, or email us at{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link> and we'll help directly.
          </Alert>
        )}

        {!loading && !error && helpCategories.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <HelpOutlineIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No help content available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Help documentation is currently being updated. Please check back later.
            </Typography>
          </Box>
        )}

        {!loading && !error && helpCategories.map((category, categoryIndex) => (
          <Accordion key={categoryIndex} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {category.category}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <List disablePadding>
                  {category.questions.map((item, questionIndex) => (
                    <React.Fragment key={questionIndex}>
                      <ListItem
                        disableGutters
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          mb: questionIndex < category.questions.length - 1 ? 2 : 0,
                        }}
                      >
                        <ListItemText
                          primary={item.question}
                          secondary={item.answer}
                          slotProps={{
                            primary: { variant: 'subtitle2', sx: { fontWeight: 600, mb: 1 } },
                          }}
                        />
                      </ListItem>
                      {questionIndex < category.questions.length - 1 && (
                        <Divider sx={{ my: 2 }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: 'footer.main',
          color: 'common.white',
          py: 4,
          textAlign: 'center',
        }}
      >
        <Container>
          <Typography variant="body2">
            &copy; 2026 Reseller Pro. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicHelpAndSupport;
